import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { SolutionsMenu } from '@components/SolutionsMenu';
import { UserAvatar } from '@account_module/components/UserAvatar';
import { SounderLogo } from '@icons/SounderLogo';

import { UsersTableController } from '@account_module/components/users-table/UsersTableControler';

import { useOrganizations } from '@account_module/hooks/useOrganizations';
import { OrganizationInfoType, IOrganization } from '@account_module/types/Organizations';

const HeaderWrapper = tw.div`w-full border-b border-solid border-ruler`;
const InnerWrapper = tw.div`flex flex-row w-full h-[58px] px-6 items-center justify-between`;
const Menu = tw.div`flex flex-row items-center justify-end p-1 rounded gap-2`;
const LeftWrapper = tw.div`flex flex-row items-center justify-start gap-2 cursor-pointer w-[250px]`;
const RightWrapper = tw.div`flex items-center justify-end w-[160px]`;
const SelectedSolution = tw.div`text-[20px] text-[#192576] font-semibold`;
const BetaLabel = tw.div`bg-muted text-accent text-[11px] px-1 rounded-sm`;
const HeaderTitle = tw.div`text-[11px] text-muted font-semibold`;
const Divider = tw.span`inline-block text-[11px] text-text-muted-2`;
const Separator = tw.div`w-[1px] h-[17px] bg-muted4`;
const Flex = tw.div`flex items-center justify-between gap-8`;
const StyledInfoHeader = tw.div`w-full flex items-center justify-between px-6 py-[18px] border-b border-solid border-ruler`;
const InfoItem = tw.span`text-xs [> span]:text-accent`;

export const INFO_HEADER_HEIGHT = '54px';
export const HEADER_HEIGHT = '61px';
export const TABLE_CONTROLER_HEIGHT = '39px';

const OrganizationInfoHeader = ({ organization }: { organization: IOrganization | undefined }) => {
  const info = useMemo((): OrganizationInfoType | undefined => {
    if (organization) {
      return {
        ...organization.show_limits,
        updated_at: new Date(organization?.updated_at).toLocaleString('en-US'),
      };
    }
  }, [organization]);

  return (
    <StyledInfoHeader>
      <Flex>
        <InfoItem>
          <Trans
            i18nKey={'account_module.settings_page.info_header.show_limit'}
            values={{ number: info?.max_shows || 0 }}>
            Show Limit: <span tw='text-accent' />
          </Trans>
        </InfoItem>
        <InfoItem>
          <Trans
            i18nKey={'account_module.settings_page.info_header.show_added'}
            values={{ number: info?.selected_shows || 0 }}>
            Show Added: <span tw='text-accent' />
          </Trans>
        </InfoItem>
        <InfoItem></InfoItem>
      </Flex>
    </StyledInfoHeader>
  );
};

type HeaderPropsType = {
  showUserTableControler?: boolean;
  showBreadchrumbs?: boolean;
  showSolutionsMenu?: boolean;
  title?: string | JSX.Element | undefined | null;
  label?: string | JSX.Element | undefined | null;
};

const Header = ({
  showUserTableControler = true,
  showBreadchrumbs = true,
  showSolutionsMenu = false,
  title,
  label,
}: HeaderPropsType): JSX.Element | null => {
  const { t } = useTranslation();
  const { isLoading, getOrganizationByIndex } = useOrganizations();
  const organization = getOrganizationByIndex(0);

  const shouldDisplaySolutionsMenu = useMemo(
    () =>
      (organization?.type === 'customer' || organization?.type === 'customer-free') &&
      !isLoading &&
      showSolutionsMenu,
    [organization, showSolutionsMenu]
  );

  if (isLoading || !organization) {
    return null;
  }

  return (
    <>
      {organization?.show_limits !== null && <OrganizationInfoHeader organization={organization} />}
      <HeaderWrapper>
        <InnerWrapper>
          <LeftWrapper>
            {shouldDisplaySolutionsMenu && <SolutionsMenu />}

            <SounderLogo width={20} height={28} tw='fill-primary' />

            <Menu>
              <SelectedSolution>{t('account_module.settings_page.header.title')}</SelectedSolution>

              {!!label && <BetaLabel>{label}</BetaLabel>}

              {showBreadchrumbs && (
                <>
                  <Divider>/</Divider>
                  <HeaderTitle>
                    {' '}
                    {t('account_module.settings_page.header.organization')}
                  </HeaderTitle>
                </>
              )}
            </Menu>
            {!!title && (
              <>
                <Separator />
                {title}
              </>
            )}
          </LeftWrapper>
          <RightWrapper>
            <UserAvatar />
          </RightWrapper>
        </InnerWrapper>
      </HeaderWrapper>
      {showUserTableControler && <UsersTableController />}
    </>
  );
};

export { Header };
